import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Modal, Btn, Input, Select, EmptyState, ConfirmDialog, CreatableSelect, ListSkeleton } from "@/components/Kit";
import { ImportDialog } from "@/components/ImportDialog";
import { usePagination, Pagination } from "@/components/Pagination";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wallet, Upload, Download, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const empty = { name: "", code: "", contact: "", company: "", organization_id: null, email: "", phone: "", rfc: "", address: "", has_credit: false, credit_days: 0, agent: "" };

export default function Clientes() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [sort, setSort] = useState({ field: "name", dir: "asc" }); // field: "name" | "org" | null
  const [orgs, setOrgs] = useState([]);
  const [newOrg, setNewOrg] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [search, setSearch] = useState("");
  const [similarPrompt, setSimilarPrompt] = useState(null);

  const toggleSort = (field) =>
    setSort((s) =>
      s.field !== field
        ? { field, dir: "asc" }
        : s.dir === "asc"
        ? { field, dir: "desc" }
        : { field: null, dir: "asc" }
    );

  const orgName = useCallback((c) => orgs.find((o) => o.id === c.organization_id)?.name || "", [orgs]);

  const sortedClients = useMemo(() => {
    let list = agentFilter ? clients.filter((c) => (c.agent || "") === agentFilter) : clients;
    if (orgFilter) list = list.filter((c) => (c.organization_id || "") === orgFilter);
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length) {
      list = list.filter((c) => {
        const hay = [c.name, c.code, c.contact, c.company, c.email, c.phone, c.rfc, c.agent]
          .filter(Boolean).join(" ").toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }
    if (!sort.field) return list;
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (c) => (sort.field === "org" ? orgName(c) : c.name || "");
    return [...list].sort(
      (a, b) => dir * val(a).localeCompare(val(b), "es", { sensitivity: "base" })
    );
  }, [clients, sort, orgs, agentFilter, search, orgFilter, orgName]);

  const pageClients = usePagination(sortedClients, 50);

  const load = () => api.get("/clients").then((r) => setClients([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })))).finally(() => setLoading(false));
  const loadOrgs = () => api.get("/organizations").then((r) => setOrgs(r.data));
  const loadAgents = () => api.get("/agents").then((r) => setAgents(r.data.map((a) => a.name)));
  useEffect(() => {
    load();
    loadOrgs();
    loadAgents();
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditing(null);
    setNewOrg("");
    setOpen(true);
  };
  const openEdit = (c) => {
    setForm({ ...empty, ...c });
    setEditing(c.id);
    setNewOrg("");
    setOpen(true);
  };

  const save = async (e, force = false) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      if (!editing && !force && (form.name || "").trim()) {
        const { data } = await api.get("/clients/check-similar", { params: { name: form.name } });
        if (data.match) {
          setSimilarPrompt(data);
          return;
        }
      }
      let organization_id = form.organization_id || null;
      if (newOrg.trim()) {
        const { data } = await api.post("/organizations", { name: newOrg.trim() });
        organization_id = data.id;
      }
      const payload = { ...form, organization_id, credit_days: Number(form.credit_days) || 0 };
      if (editing) await api.put(`/clients/${editing}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Cliente actualizado" : "Cliente creado");
      setSimilarPrompt(null);
      setOpen(false);
      setNewOrg("");
      load();
      loadOrgs();
      loadAgents();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createAgent = async (name) => {
    try {
      await api.post("/agents", { name });
      const r = await api.get("/agents");
      setAgents(r.data.map((a) => a.name));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!deleteOpen) return;
    try {
      await api.delete(`/clients/${deleteOpen.id}`);
      toast.success("Cliente eliminado");
      setDeleteOpen(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHeader
        overline="Directorio"
        title="Clientes"
        action={
          <div className="flex gap-3">
            <Btn variant="outline" onClick={() => setExportOpen(true)} data-testid="export-clients-button">
              <Download size={16} /> Exportar
            </Btn>
            <Btn variant="outline" onClick={() => setImportOpen(true)} data-testid="import-clients-button">
              <Upload size={16} /> Importar
            </Btn>
            <Btn onClick={openNew} data-testid="new-client-button">
              <Plus size={16} /> Nuevo cliente
            </Btn>
          </div>
        }
      />
      <div className="p-10">
        {loading ? (
          <ListSkeleton testid="clientes-loading" />
        ) : clients.length === 0 ? (
          <EmptyState
            title="Sin clientes aún"
            subtitle="Agrega tu primer cliente para empezar a crear cotizaciones."
            action={<Btn onClick={openNew}><Plus size={16} /> Nuevo cliente</Btn>}
          />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-80">
                <label className="block">
                  <span className="overline block mb-1.5">Buscar cliente</span>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Ej. alta prevencion cesar"
                    data-testid="search-client"
                    className="w-full bg-[#FFFFFF] border border-[#E3E8EE] px-3 py-2 font-mono-data text-sm text-[#1C1A17] focus:outline-none focus:ring-1 focus:ring-[#465B7F] focus:border-[#465B7F]"
                  />
                </label>
              </div>
              <div className="w-72">
                <Select label="Filtrar por agente" value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)} testid="filter-agent">
                  <option value="">Todos los agentes</option>
                  <option value="__none__" disabled>──────────</option>
                  {agents.map((a) => <option key={a} value={a}>{a}</option>)}
                </Select>
              </div>
              <div className="w-72">
                <Select label="Filtrar por organización" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} testid="filter-organization">
                  <option value="">Todas las organizaciones</option>
                  <option value="__none__" disabled>──────────</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </Select>
              </div>
              {(agentFilter || search || orgFilter) && (
                <button type="button" onClick={() => { setAgentFilter(""); setSearch(""); setOrgFilter(""); }} data-testid="clear-agent-filter"
                  className="mt-6 font-mono-data text-xs uppercase tracking-wider text-[#5B6672] hover:text-[#465B7F]">
                  Limpiar filtro
                </button>
              )}
            </div>
          <div className="border border-[#E3E8EE] bg-[#FFFFFF] data-scroll">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E3E8EE] text-left">
                  <Th>
                    <button
                      type="button"
                      onClick={() => toggleSort("name")}
                      data-testid="sort-name-button"
                      className="inline-flex items-center gap-1.5 uppercase tracking-widest hover:text-[#465B7F] transition-colors"
                      title="Ordenar alfabéticamente"
                    >
                      Nombre Cliente
                      {sort.field === "name" && sort.dir === "asc" ? (
                        <ArrowUp size={13} className="text-[#465B7F]" />
                      ) : sort.field === "name" && sort.dir === "desc" ? (
                        <ArrowDown size={13} className="text-[#465B7F]" />
                      ) : (
                        <ArrowUpDown size={13} className="opacity-50" />
                      )}
                    </button>
                  </Th>
                  <Th>
                    <button
                      type="button"
                      onClick={() => toggleSort("org")}
                      data-testid="sort-org-button"
                      className="inline-flex items-center gap-1.5 uppercase tracking-widest hover:text-[#465B7F] transition-colors"
                      title="Ordenar por organización"
                    >
                      Organización
                      {sort.field === "org" && sort.dir === "asc" ? (
                        <ArrowUp size={13} className="text-[#465B7F]" />
                      ) : sort.field === "org" && sort.dir === "desc" ? (
                        <ArrowDown size={13} className="text-[#465B7F]" />
                      ) : (
                        <ArrowUpDown size={13} className="opacity-50" />
                      )}
                    </button>
                  </Th>
                  <Th>ID</Th>
                  <Th>Contacto</Th>
                  <Th>Agente</Th>
                  <Th>Correo</Th>
                  <Th>Teléfono</Th>
                  <Th>Crédito</Th>
                  <Th className="text-right">Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {pageClients.pageItems.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`client-row-${c.id}`}
                    className="border-b border-[#E3E8EE] last:border-0 hover:bg-[#E9EEF3] transition-colors"
                  >
                    <Td className="text-[#16202B] font-medium">{c.name}</Td>
                    <Td data-testid={`client-org-${c.id}`}>{orgs.find((o) => o.id === c.organization_id)?.name || "—"}</Td>
                    <Td className="text-[#465B7F] font-medium" data-testid={`client-code-${c.id}`}>{c.code || "—"}</Td>
                    <Td>{c.contact || "—"}</Td>
                    <Td data-testid={`client-agent-${c.id}`}>{c.agent || "—"}</Td>
                    <Td>{c.email || "—"}</Td>
                    <Td>{c.phone || "—"}</Td>
                    <Td>{c.has_credit ? `${c.credit_days || 0} días` : "Contado"}</Td>
                    <Td className="text-right">
                      <button onClick={() => navigate(`/clientes/${c.id}/estado`)} title="Estado de cuenta" data-testid={`account-client-${c.id}`} className="text-[#5B6672] hover:text-[#465B7F] mr-4">
                        <Wallet size={15} />
                      </button>
                      <button onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`} className="text-[#5B6672] hover:text-[#465B7F] mr-4">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => setDeleteOpen(c)} data-testid={`delete-client-${c.id}`} className="text-[#5B6672] hover:text-[#ED1F26]">
                        <Trash2 size={15} />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination {...pageClients} testid="pagination-clients" />
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar cliente" : "Nuevo cliente"}>
        <form onSubmit={save} className="space-y-4">
          <Input label="Nombre *" value={form.name} onChange={set("name")} required testid="client-name" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="ID *" value={form.code} onChange={set("code")} required testid="client-code" placeholder="Ej. ID-EB-01" />
            <Input label="Contacto" value={form.contact} onChange={set("contact")} testid="client-contact" placeholder="Persona de contacto" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="RFC" value={form.rfc} onChange={set("rfc")} testid="client-rfc" />
            <Input label="Correo" type="email" value={form.email} onChange={set("email")} testid="client-email" />
          </div>
          <Input label="Teléfono" value={form.phone} onChange={set("phone")} testid="client-phone" />
          <Input label="Dirección" value={form.address} onChange={set("address")} testid="client-address" />
          <CreatableSelect
            label="Agente"
            value={form.agent || ""}
            onChange={(v) => setForm((f) => ({ ...f, agent: v }))}
            options={agents}
            onCreate={createAgent}
            placeholder="Sin agente / selecciona…"
            addLabel="➕ Agregar nuevo agente…"
            newPlaceholder="Nuevo agente…"
            testid="client-agent"
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Organización"
              value={form.organization_id || ""}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value || null })}
              testid="client-organization"
            >
              <option value="">Sin organización</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </Select>
            <Input
              label="+ Nueva organización"
              value={newOrg}
              onChange={(e) => setNewOrg(e.target.value)}
              placeholder="Crear y asignar"
              testid="client-new-organization"
            />
          </div>
          <div className="border-t border-[#E3E8EE] pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.has_credit} data-testid="client-has-credit"
                onChange={(e) => setForm({ ...form, has_credit: e.target.checked })}
                className="w-4 h-4 accent-[#465B7F]" />
              <span className="font-mono-data text-sm text-[#16202B]">Otorgar días de crédito</span>
            </label>
            {form.has_credit && (
              <div className="mt-3 w-40">
                <Input label="Días de crédito" type="number" min="0" value={form.credit_days} onChange={set("credit_days")} testid="client-credit-days" />
              </div>
            )}
            <p className="font-mono-data text-xs text-[#5B6672] mt-2">
              Define el vencimiento de remisiones y facturas. Sin crédito, vence el mismo día de emisión.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-client-button">Guardar</Btn>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteOpen}
        onClose={() => setDeleteOpen(null)}
        onConfirm={remove}
        title="Eliminar cliente"
        message={`¿Seguro que deseas eliminar a ${deleteOpen?.name}? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar cliente"
        testid="confirm-delete-client-button"
      />

      <Modal open={!!similarPrompt} onClose={() => setSimilarPrompt(null)} title="Cliente similar encontrado">
        <div className="space-y-4" data-testid="similar-client-prompt">
          <p className="font-mono-data text-sm text-[#5C5A55]">
            Ya existe un cliente con un nombre casi idéntico
            {similarPrompt?.similarity ? ` (${similarPrompt.similarity}% de coincidencia)` : ""}.
            ¿Quieres usar el cliente existente o crear uno nuevo de todos modos?
          </p>
          <div className="border border-[#465B7F]/30 bg-[#465B7F]/5 p-3">
            <div className="overline mb-0.5">Cliente existente</div>
            <div className="font-mono-data text-sm text-[#16202B] font-semibold" data-testid="similar-client-name">
              {similarPrompt?.match?.name}
            </div>
            {similarPrompt?.match?.code && (
              <div className="font-mono-data text-xs text-[#5B6672]">ID: {similarPrompt.match.code}</div>
            )}
            {similarPrompt?.match?.company && (
              <div className="font-mono-data text-xs text-[#5B6672]">{similarPrompt.match.company}</div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => save(null, true)} data-testid="create-anyway-button">
              Crear nuevo de todos modos
            </Btn>
            <Btn type="button" onClick={() => {
              const m = similarPrompt?.match;
              setSimilarPrompt(null);
              setOpen(false);
              setNewOrg("");
              if (m) navigate(`/clientes/${m.id}/estado`);
            }} data-testid="use-existing-client-button">
              Usar cliente existente
            </Btn>
          </div>
        </div>
      </Modal>

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="clients"
        title="Importar clientes"
        onDone={load}
      />

      <Modal open={exportOpen} onClose={() => setExportOpen(false)} title="Exportar clientes">
        <p className="font-mono-data text-sm text-[#5C5A55] mb-4">Elige qué lista de clientes descargar (Excel):</p>
        <div className="grid gap-3">
          {[
            ["all", "Todos los clientes", "Directorio completo con saldos."],
            ["overdue", "Con saldo vencido", "Solo clientes con facturas vencidas por cobrar."],
            ["current", "Al corriente", "Clientes sin saldo vencido."],
          ].map(([key, label, desc]) => (
            <button
              key={key}
              data-testid={`export-clients-${key}`}
              onClick={() => { window.open(`/api/clients/export?filter=${key}`, "_blank"); setExportOpen(false); }}
              className="flex items-start gap-3 border border-[#E3E8EE] p-4 text-left hover:border-[#465B7F] hover:bg-[#E9EEF3] transition-colors"
            >
              <Download size={18} className="text-[#465B7F] mt-0.5" />
              <span>
                <span className="block font-mono-data text-sm text-[#16202B] font-semibold">{label}</span>
                <span className="block font-mono-data text-xs text-[#5C5A55]">{desc}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="flex justify-end mt-5">
          <Btn variant="ghost" type="button" onClick={() => setExportOpen(false)}>Cancelar</Btn>
        </div>
      </Modal>
    </>
  );
}

function Th({ children, className = "" }) {
  return <th className={`overline font-normal px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`font-mono-data text-sm text-[#5C5A55] px-4 py-3 ${className}`}>{children}</td>;
}
